/* ---
  Set-Auswahl in der Kaufbox (snippets/bundle-offer.liquid)

  Schreibt bei gewähltem Set dessen Varianten-ID in das id-Feld des bestehenden
  Produktformulars, setzt die Menge auf 1 und den Knopf-Text auf „Add set to cart – €…".
  Zurück auf Option 1 stellt alles wieder her. Das Absenden (AJAX oder klassisch)
  bleibt Sache der jeweiligen Produktsektion.

  Funktioniert mit allen Kaufboxen des Themes:
  - main-product, main-product2, ec-selection-product, ec-albino-product
    (<product-variants> schreibt das id-Feld und meldet das mit einem change-Event)
  - ec-lf-product, ec-vod-product (Größen-Chips [data-variant-id] mit eigenem Skript)

  Größenwechsel bei aktivem Set: Der Zustand wird in der Capture-Phase — also vor den
  Skripten der Sektion — zurückgesetzt; danach schreibt die Sektion wie gewohnt die neue
  Variante, Preis und Knopf-Text. Option 1 liest anschließend das id-Feld.
--- */

if ( typeof BundleOffer !== 'function' ) {

  class BundleOffer extends HTMLElement {

    connectedCallback() {
      if ( this._ready ) return;
      this.form = document.getElementById(this.dataset.form);
      if ( ! this.form ) return;
      this._ready = true;

      this.data = JSON.parse(this.querySelector('[data-offer-data]').textContent);
      this.single = this.querySelector('[data-offer-single]');
      this.singleTitle = this.querySelector('[data-offer-single-title]');
      this.singlePrice = this.querySelector('[data-offer-single-price]');
      this.active = null;

      this.link = this.querySelector('[data-offer-link]').content.firstElementChild.cloneNode(true);
      this.link.hidden = true;
      this.form.insertAdjacentElement('afterend', this.link);

      this.querySelectorAll('.ec-offer__radio').forEach(radio => {
        radio.addEventListener('change', () => {
          if ( radio.checked ) this.select(radio);
        });
      });

      this.scope = this.closest('.shopify-section') || document;
      this.onVariantIntent = this.onVariantIntent.bind(this);
      this.scope.addEventListener('change', this.onVariantIntent, true);
      this.scope.addEventListener('click', this.onVariantIntent, true);

      // <product-variants> meldet jede neue Variante als change auf dem id-Feld
      this.form.addEventListener('change', e => {
        if ( e.target.name == 'id' ) this.refreshSingle();
      });

      this.refreshSingle();
    }

    disconnectedCallback() {
      if ( this.scope ) {
        this.scope.removeEventListener('change', this.onVariantIntent, true);
        this.scope.removeEventListener('click', this.onVariantIntent, true);
      }
      if ( this.link ) this.link.remove();
      this._ready = false;
    }

    get idInput() { return this.form.querySelector('input[name="id"]'); }
    get qtyInput() { return this.form.querySelector('[name="quantity"]'); }
    get button() { return this.form.querySelector('[type="submit"][name="add"]') || this.form.querySelector('[type="submit"]'); }
    get label() {
      const button = this.button;
      if ( ! button ) return null;
      return button.querySelector('[data-js-product-add-to-cart-text], [data-lf-atc-text], [data-vod-atc-text]') || button;
    }

    onVariantIntent(e) {
      const target = e.target;
      if ( ! target || ! target.closest || this.contains(target) ) return;
      const chip = target.closest('button[data-variant-id], [role="radio"][data-variant-id]');
      const picker = target.closest('product-variants');
      const isIntent = ( e.type == 'click' && chip ) || ( e.type == 'change' && picker );
      if ( ! isIntent ) return;
      if ( this.active ) {
        this.restore(target);
        this.single.checked = true;
      }
      setTimeout(() => this.refreshSingle(), 0);
    }

    select(radio) {
      if ( radio == this.single ) {
        this.restore();
        return;
      }
      const idInput = this.idInput;
      if ( ! idInput ) return;
      if ( ! this.active ) {
        this.save();
        this.clearVariantSelection();
      }
      this.active = radio;

      idInput.value = radio.value;
      if ( this.qtyInput ) this.qtyInput.value = 1;

      const button = this.button;
      if ( button ) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('disabled');
      }
      this.form.classList.remove('disabled-cart');
      if ( this.label ) this.label.textContent = radio.dataset.label;
      this.showSetPrice(radio.dataset.price);

      this.link.href = radio.dataset.url;
      this.link.hidden = false;

      if ( window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish == 'function' ) {
        Shopify.analytics.publish('bundle_selected', {
          bundle_handle: radio.dataset.handle,
          source_product: this.dataset.sourceProduct
        });
      }
    }

    save() {
      const button = this.button;
      this.saved = {
        id: this.idInput.value,
        qty: this.qtyInput ? this.qtyInput.value : null,
        label: this.label ? this.label.innerHTML : null,
        disabled: button ? button.disabled : false,
        ariaDisabled: button ? button.getAttribute('aria-disabled') : null,
        disabledClass: button ? button.classList.contains('disabled') : false,
        disabledCart: this.form.classList.contains('disabled-cart')
      };
    }

    restore(trigger) {
      if ( ! this.active || ! this.saved ) return;
      this.restoreVariantSelection(trigger);
      const saved = this.saved;
      const button = this.button;
      this.idInput.value = saved.id;
      if ( this.qtyInput && saved.qty !== null ) this.qtyInput.value = saved.qty;
      if ( this.label && saved.label !== null ) this.label.innerHTML = saved.label;
      if ( button ) {
        button.disabled = saved.disabled;
        if ( saved.ariaDisabled === null ) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', saved.ariaDisabled);
        button.classList.toggle('disabled', saved.disabledClass);
      }
      this.form.classList.toggle('disabled-cart', saved.disabledCart);
      this.restorePrice();
      this.link.hidden = true;
      this.active = null;
      this.saved = null;
    }

    // Preis oben in der Kaufbox: Bei gewähltem Set steht dort der Set-Preis statt des
    // Dosenpreises (vorher blieb z. B. €54,00 stehen, während „For Two" €99 kostet).
    // Streichpreis und Grundpreis der Dose werden so lange ausgeblendet.
    priceNodes() {
      const scope = this.scope || document;
      return {
        prices: Array.from(scope.querySelectorAll('[data-js-product-price-original], [data-lf-price], [data-vod-price]')),
        extras: Array.from(scope.querySelectorAll('[data-js-product-price-compare], [data-js-product-price-unit]'))
      };
    }

    showSetPrice(price) {
      if ( ! price ) return;
      const nodes = this.priceNodes();
      if ( ! this.savedPrices ) {
        this.savedPrices = nodes.prices.map(el => [el, el.innerHTML]);
        this.savedExtras = nodes.extras.map(el => [el, el.style.display]);
      }
      nodes.prices.forEach(el => { el.textContent = price; });
      nodes.extras.forEach(el => { el.style.display = 'none'; });
    }

    restorePrice() {
      ( this.savedPrices || [] ).forEach(([el, html]) => { el.innerHTML = html; });
      ( this.savedExtras || [] ).forEach(([el, display]) => { el.style.display = display; });
      this.savedPrices = null;
      this.savedExtras = null;
    }

    // Bei gewähltem Set darf keine Dosengröße markiert bleiben: gewählte Größe
    // abwählen (Radios in main-product & Co., Chips in ec-lf-/ec-vod-product)
    // und beim Zurückwechseln wieder herstellen. Klickt der Kunde selbst eine
    // Größe an, bleibt seine Wahl — die alte wird dann nicht zurückgesetzt.
    clearVariantSelection() {
      const scope = this.scope || document;
      this.clearedInputs = Array.from(scope.querySelectorAll('.product-variant__input:checked'));
      this.clearedInputs.forEach(input => { input.checked = false; });
      this.clearedChips = Array.from(scope.querySelectorAll('[data-variant-id].is-selected'));
      this.clearedChips.forEach(chip => {
        chip.classList.remove('is-selected');
        chip.setAttribute('aria-checked', 'false');
      });
    }

    restoreVariantSelection(trigger) {
      const group = trigger && trigger.matches && trigger.matches('.product-variant__input') ? trigger.name : null;
      const chipClicked = trigger && trigger.closest && trigger.closest('[data-variant-id]');
      ( this.clearedInputs || [] ).forEach(input => {
        if ( input.name !== group ) input.checked = true;
      });
      if ( ! chipClicked ) {
        ( this.clearedChips || [] ).forEach(chip => {
          chip.classList.add('is-selected');
          chip.setAttribute('aria-checked', 'true');
        });
      }
      this.clearedInputs = [];
      this.clearedChips = [];
    }

    refreshSingle() {
      if ( this.active ) return;
      const idInput = this.idInput;
      if ( ! idInput ) return;
      const variant = this.data.variants.find(v => String(v.id) == String(idInput.value));
      if ( ! variant ) return;
      this.singleTitle.textContent = this.data.hasVariants ? `${this.data.title} · ${variant.title}` : this.data.title;
      this.singlePrice.textContent = variant.available ? variant.price : this.data.soldOut;
    }

  }

  if ( typeof customElements.get('bundle-offer') == 'undefined' ) {
    customElements.define('bundle-offer', BundleOffer);
  }

}
